import { useCallback, useEffect, useRef, useState } from 'react'

import { useFeatureFlagsContext } from '@/context'

export type PermissionState = 'granted' | 'denied' | 'prompt'

export interface GeolocationInput {
  permissionState: PermissionState
  latitude?: number
  longitude?: number
  accuracy?: number
  altitude?: number
  errorCode?: number
}

/**
 * Returns a `captureGeolocation` function that resolves with a `GeolocationInput`
 * suitable for passing directly to mutations that accept a `geolocation` argument.
 *
 * Gated by the `geolocation` feature flag. When disabled, `captureGeolocation`
 * resolves immediately with `undefined` so callers can omit the field from their
 * mutation payload and Rusty does not emit geolocation events.
 */
export function useGeolocation() {
  const { isEnabled } = useFeatureFlagsContext()
  const geolocationEnabled = isEnabled('geolocation')

  const [permissionState, setPermissionState] = useState<PermissionState>('prompt')
  const permissionRef = useRef<PermissionStatus | null>(null)

  useEffect(() => {
    if (!geolocationEnabled || !navigator.permissions) return

    let cancelled = false
    let cleanup: (() => void) | null = null

    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      if (cancelled) return
      permissionRef.current = status
      setPermissionState(status.state as PermissionState)

      const handleChange = () => setPermissionState(status.state as PermissionState)
      status.addEventListener('change', handleChange)
      cleanup = () => status.removeEventListener('change', handleChange)
    })

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [geolocationEnabled])

  const captureGeolocation = useCallback(async (): Promise<GeolocationInput | undefined> => {
    if (!geolocationEnabled || !navigator.geolocation) {
      return undefined
    }

    return new Promise<GeolocationInput>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            permissionState: 'granted',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude ?? undefined,
          })
        },
        (error) => {
          const currentState = permissionRef.current?.state as PermissionState | undefined
          const state: PermissionState = error.code === error.PERMISSION_DENIED ? 'denied' : (currentState ?? 'prompt')
          resolve({
            permissionState: state,
            errorCode: error.code,
          })
        },
        { timeout: 10000, maximumAge: 60000 },
      )
    })
  }, [geolocationEnabled])

  return { captureGeolocation, permissionState }
}
